import React from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Icon,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { MdAccessTime, MdClose, MdOutlineFireTruck, MdOutlineMedicalServices, MdPerson } from 'react-icons/md';

const MissionNonAccepteeNotification = ({ 
  mission, 
  user, 
  userType, 
  onClose, 
  remainingCount = 0,
  onViewMission, 
  onReassignMission 
}) => {
  // Vérifications de sécurité
  if (!mission || !user) {
    return null;
  }
  // Déterminer l'icône selon le type d'utilisateur
  const getUserIcon = () => {
    switch (userType) {
      case 'ambulancier':
        return MdOutlineFireTruck;
      case 'docteur':
        return MdOutlineMedicalServices;
      case 'infirmier':
        return MdPerson;
      default:
        return MdPerson;
    }
  };

  // Déterminer le label selon le type d'utilisateur
  const getUserLabel = () => {
    switch (userType) {
      case 'ambulancier':
        return 'Ambulancier:';
      case 'docteur':
        return 'Médecin:';
      case 'infirmier':
        return 'Infirmier:';
      default:
        return 'Personnel:';
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 devrait être 12
    const hoursStr = String(hours).padStart(2, '0');
    return `${day}/${month}/${year} at ${hoursStr}:${minutes} ${ampm}`;
  };

  // Calculer le retard
  const calculateDelay = (missionDate) => {
    const now = new Date();
    const date = new Date(missionDate);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 1000 / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays}j ${diffHours % 24}h ${diffMins % 60}min`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMins % 60}min`;
    } else {
      return `${diffMins}min`;
    }
  };

  const UserIcon = getUserIcon();
  const userLabel = getUserLabel();
  const delay = calculateDelay(mission.date_mission);

  return (
    <Box
      bg="#FFFBEa"
      border="1px solid rgba(246,177,0,0.2)"
      borderRadius="12px"
      boxShadow="0px 4px 30px 0px rgba(0,0,0,0.1)"
      w="100%"
      position="relative"
      overflow="hidden"
    >
      {/* Barre de progression */}
      <Box
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="3px"
        bg="#D9D9D9"
      >
        <Box
          h="100%"
          bg="#F6B100"
          w="99.38%"
        />
      </Box>

      {/* Header */}
      <Flex
        borderBottom="1px solid rgba(246,177,0,0.2)"
        gap="9px"
        alignItems="flex-start"
        pb="10px"
        pt="14px"
        px="14px"
        position="relative"
      >
        <Flex flex="1" gap="9px" alignItems="center">
          <Box
            bg="rgba(246,177,0,0.2)"
            borderRadius="100px"
            p="10px"
            w="34px"
            h="34px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={MdAccessTime} w="20px" h="20px" color="#F6B100" />
          </Box>
          <VStack align="flex-start" spacing="3px" flex="1">
            <Text
              fontSize="14px"
              fontWeight="600"
              fontFamily="Poppins, sans-serif"
              color="#171D28"
            >
              Mission non acceptée
            </Text>
            <Text
              fontSize="13px"
              fontWeight="400"
              fontFamily="Poppins, sans-serif"
              color="#656C7A"
            >
              Aucune réponse après 1 min
              {remainingCount > 0 && ` • ${remainingCount + 1} en attente`}
            </Text>
          </VStack>
        </Flex>
        <Text
          fontSize="12px"
          fontWeight="500"
          fontFamily="Poppins, sans-serif"
          color="#656C7A"
          opacity="0.97"
          textAlign="center"
        >
          {formatDate(mission.date_mission)}
        </Text>
        <Box
          as="button"
          onClick={onClose}
          cursor="pointer"
          w="14px"
          h="14px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ opacity: 0.7 }}
        >
          <Icon as={MdClose} w="14px" h="14px" color="#656C7A" />
        </Box>
      </Flex>

      {/* Section Personnel */}
      <Flex
        borderBottom="1px solid rgba(246,177,0,0.2)"
        gap="8px"
        alignItems="center"
        p="14px"
      >
        <Box
          position="relative"
          w="40px"
          h="40px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            border="1px solid #E5E5E5"
            borderRadius="6px"
            w="36px"
            h="36px"
            bg="transparent"
          />
          <Box
            position="absolute"
            left="50%"
            top="50%"
            transform="translate(-50%, -50%)"
            w="20px"
            h="20px"
          >
            <Icon as={UserIcon} w="20px" h="20px" color="#656C7A" />
          </Box>
        </Box>
        <VStack align="flex-start" spacing="2px" flex="1">
          <Text
            fontSize="14px"
            fontWeight="500"
            fontFamily="Poppins, sans-serif"
            color="#656C7A"
          >
            {userLabel}
          </Text>
          <HStack spacing="10px">
            <Text
              fontSize="14px"
              fontWeight="400"
              fontFamily="Poppins, sans-serif"
              color="#171D28"
            >
              {user.prenom} {user.nom}
            </Text>
            <Text
              fontSize="14px"
              fontWeight="400"
              fontFamily="Poppins, sans-serif"
              color="#171D28"
            >
              ID: P-{String(user.id).padStart(3, '0')}
            </Text>
          </HStack>
        </VStack>
      </Flex>

      {/* Footer avec boutons */}
      <Flex
        bg="#FFF9DE"
        gap="10px"
        alignItems="center"
        justifyContent="flex-end"
        px="14px"
        py="10px"
      >
        <Button
          variant="ghost"
          h="32px"
          px="16px"
          py="13px"
          borderRadius="6px"
          onClick={onViewMission}
          _hover={{ bg: 'rgba(232, 112, 0, 0.1)' }}
        >
          <Text
            fontSize="12px"
            fontWeight="500"
            fontFamily="Poppins, sans-serif"
            color="#E87000"
            letterSpacing="-0.12px"
          >
            Voir la mission
          </Text>
        </Button>
        <Button
          bg="#E87000"
          h="32px"
          px="16px"
          py="13px"
          borderRadius="6px"
          onClick={onReassignMission}
          _hover={{ bg: '#D86500' }}
        >
          <Text
            fontSize="12px"
            fontWeight="600"
            fontFamily="Poppins, sans-serif"
            color="white"
            letterSpacing="-0.12px"
          >
            Réssigner la mission
          </Text>
        </Button>
      </Flex>
    </Box>
  );
};

export default MissionNonAccepteeNotification;

